import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Building2, Mail, Save, Shield, Globe, Upload, X, ImageIcon, Tags, Plus, Sparkles, MapPin, AlertTriangle } from 'lucide-react';
import { authAPI, companyAPI, socialMediaAPI } from '@/lib/api';
import { useT } from '@/lib/LanguageContext';
import { useModules, hasModule } from '@/lib/modules';

const EMPTY_ADDRESS = { street: '', street2: '', city: '', region: '', postal_code: '', country: '' };

const formatAddress = (a) => {
  if (!a) return '';
  const regionPostal = [a.region, a.postal_code].filter(Boolean).join(' ');
  return [a.street, a.street2, a.city, regionPostal, a.country].filter((p) => p && p.trim()).join(', ');
};

const isAddressComplete = (a) => !!(a && a.street?.trim() && a.city?.trim() && a.country?.trim());

export default function CompanySettings({ embedded = false }) {
  const t = useT();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const [form, setForm] = useState({ name: '', slug: '', mailing_address: { ...EMPTY_ADDRESS } });

  // Contact types state
  const [contactTypes, setContactTypes] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [savingTypes, setSavingTypes] = useState(false);
  const [aiTranslationEnabled, setAiTranslationEnabled] = useState(false);
  const modules = useModules();
  const [savingAiTranslation, setSavingAiTranslation] = useState(false);

  useEffect(() => { loadCompany(); }, []);

  const loadCompany = async () => {
    try {
      const res = await authAPI.getMe();
      const userData = res.data;
      if (userData?.company) {
        // Prefer locally-stored data URL (works cross-origin); fall back to server URL
        // Key is scoped to company ID so different tenants don't bleed into each other
        const companyId = userData.company.id;
        const localLogo = localStorage.getItem(`heyday_company_logo_${companyId}`) || '';
        const companyWithLogo = {
          ...userData.company,
          logo_url: localLogo || userData.company.logo_url || null,
        };
        setCompany(companyWithLogo);
        setForm({
          name: userData.company.name || '',
          slug: userData.company.slug || '',
          mailing_address: { ...EMPTY_ADDRESS, ...(userData.company.mailing_address || {}) },
        });
        setContactTypes(userData.company.contact_types || []);
        setAiTranslationEnabled(!!userData.company.ai_translation_enabled);
      }
    } catch (err) {
      console.error('Failed to load company:', err);
      toast.error('Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Company name is required'); return; }
    const addr = form.mailing_address;
    const anyAddr = Object.values(addr).some((v) => v && v.trim());
    if (anyAddr && !isAddressComplete(addr)) {
      toast.error('Mailing address needs at least a street, city and country');
      return;
    }
    setSaving(true);
    try {
      const res = await companyAPI.updateMe({ name: form.name, mailing_address: addr });
      const updated = { ...company, ...res.data };
      setCompany(updated);
      // Sync to localStorage so TopBar/Sidebar pick it up immediately
      try {
        const stored = JSON.parse(localStorage.getItem('heyday_user') || '{}');
        localStorage.setItem('heyday_user', JSON.stringify({ ...stored, company: updated }));
      } catch {}
      toast.success('Company settings saved');
      setEditing(false);
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleAiTranslation = async (checked) => {
    setAiTranslationEnabled(checked); // optimistic
    setSavingAiTranslation(true);
    try {
      await companyAPI.updateMe({ ai_translation_enabled: checked });
      const updated = { ...company, ai_translation_enabled: checked };
      setCompany(updated);
      try {
        const stored = JSON.parse(localStorage.getItem('heyday_user') || '{}');
        localStorage.setItem('heyday_user', JSON.stringify({ ...stored, company: updated }));
      } catch {}
      toast.success(checked ? 'AI translation enabled' : 'AI translation disabled');
    } catch {
      setAiTranslationEnabled(!checked); // revert
      toast.error('Failed to update AI translation setting');
    } finally {
      setSavingAiTranslation(false);
    }
  };

  const addContactType = () => {
    const trimmed = newTypeName.trim();
    if (!trimmed) return;
    if (contactTypes.includes(trimmed)) { toast.error('That type already exists'); return; }
    setContactTypes(prev => [...prev, trimmed]);
    setNewTypeName('');
  };

  const removeContactType = (label) => {
    setContactTypes(prev => prev.filter(t => t !== label));
  };

  const saveContactTypes = async () => {
    setSavingTypes(true);
    try {
      await companyAPI.updateMe({ contact_types: contactTypes });
      toast.success('Target types saved');
    } catch {
      toast.error('Failed to save target types');
    } finally {
      setSavingTypes(false);
    }
  };

  // Resize image to max 400px wide / 120px tall, return compressed data URL
  const resizeToDataUrl = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      const MAX_W = 400, MAX_H = 120;
      let { width: w, height: h } = img;
      const ratio = Math.min(MAX_W / w, MAX_H / h, 1);
      w = Math.round(w * ratio);
      h = Math.round(h * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(objectUrl);
      resolve(canvas.toDataURL('image/png', 0.9));
    };
    img.onerror = reject;
    img.src = objectUrl;
  });

  const syncLogo = (dataUrl) => {
    try {
      const user = JSON.parse(localStorage.getItem('heyday_user') || '{}');
      const companyId = user?.company_id || company?.id;
      if (companyId) {
        localStorage.setItem(`heyday_company_logo_${companyId}`, dataUrl || '');
      }
      // Remove legacy unscoped key to prevent cross-tenant bleed
      localStorage.removeItem('heyday_company_logo');
      window.dispatchEvent(new Event('heyday_company_updated'));
    } catch {}
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo must be under 5 MB'); return; }

    setUploadingLogo(true);
    try {
      // Resize and store as data URL for reliable cross-origin display
      const dataUrl = await resizeToDataUrl(file);

      // Show immediately in UI
      const updated = { ...company, logo_url: dataUrl };
      setCompany(updated);
      syncLogo(dataUrl);

      // Also persist to server (best-effort) so it survives localStorage clears
      try {
        const uploadRes = await socialMediaAPI.upload(file);
        const mediaId = uploadRes.data?.id;
        if (mediaId) {
          const serverUrl = socialMediaAPI.serveUrl(mediaId);
          await companyAPI.updateMe({ logo_url: serverUrl });
        }
      } catch (serverErr) {
        console.warn('Server logo save failed, using local only:', serverErr);
      }

      toast.success('Logo updated');
    } catch (err) {
      console.error('Logo upload failed:', err);
      toast.error('Logo upload failed — please try again');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await companyAPI.updateMe({ logo_url: null });
      const updated = { ...company, logo_url: null };
      setCompany(updated);
      syncLogo('');
      toast.success('Logo removed');
    } catch {
      toast.error('Failed to remove logo');
    }
  };

  const Wrap = embedded ? React.Fragment : Layout;
  const wrapProps = embedded ? {} : { title: t('settings.company_title') };

  if (loading) {
    return (
      <Wrap {...wrapProps}>
        <div className="flex items-center justify-center h-96">
          <p className="text-[#5C6670]">Loading company settings...</p>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap {...wrapProps}>
      <div className={embedded ? 'max-w-4xl' : 'container mx-auto p-8 max-w-4xl'}>
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('settings.company_title')}
          </h1>
          <p className="text-muted-foreground">Manage your company profile and branding.</p>
        </div>

        {/* Company Logo Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <ImageIcon className="w-5 h-5 text-[#A62B1F]" />
              <div>
                <CardTitle>{t('settings.company_logo')}</CardTitle>
                <CardDescription>Shown in the top header. PNG, JPG, or SVG — max 5 MB.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              {/* Preview */}
              <div
                className="w-20 h-20 rounded-xl border-2 border-dashed border-[#E3DDD1] flex items-center justify-center overflow-hidden bg-[#FBF8F2] flex-shrink-0 cursor-pointer hover:border-[#A62B1F] transition-colors"
                onClick={() => logoInputRef.current?.click()}
                title="Click to upload logo"
              >
                {company?.logo_url ? (
                  <img src={company.logo_url} alt="Company logo" className="w-full h-full object-contain p-1" />
                ) : (
                  <Building2 className="w-8 h-8 text-[#D3CCBE]" />
                )}
              </div>

              <div className="space-y-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingLogo ? t('common.uploading') : company?.logo_url ? 'Replace Logo' : t('settings.upload_logo')}
                </Button>
                {company?.logo_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 block"
                    onClick={handleRemoveLogo}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                )}
                <p className="text-xs text-[#5C6670]">Click the logo preview or the button to upload.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Profile Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-[#A62B1F]" />
                <div>
                  <CardTitle>Company Profile</CardTitle>
                  <CardDescription>Your organization details</CardDescription>
                </div>
              </div>
              {!editing && (
                <Button variant="outline" onClick={() => setEditing(true)}>{t('common.edit')}</Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {editing ? (
              <>
                <div>
                  <Label htmlFor="company-name">{t('settings.company_name')} *</Label>
                  <Input
                    id="company-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="MedWorks Inc."
                  />
                </div>
                <div>
                  <Label htmlFor="company-slug">Slug</Label>
                  <Input
                    id="company-slug"
                    value={form.slug}
                    disabled
                    className="bg-muted text-muted-foreground"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Slug is set at account creation and cannot be changed here.</p>
                </div>
                <div className="pt-2">
                  <Label>Mailing Address</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Shown in the footer of every campaign email. US (CAN-SPAM) and Canadian (CASL) law require a valid
                    physical postal address — a street address, PO box, or registered private mailbox.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      ['street', 'Street address', '123 King St W', 'sm:col-span-2'],
                      ['street2', 'Suite / unit (optional)', 'Suite 400', 'sm:col-span-2'],
                      ['city', 'City', 'Toronto', ''],
                      ['region', 'Province / state', 'ON', ''],
                      ['postal_code', 'Postal / ZIP code', 'M5H 1A1', ''],
                      ['country', 'Country', 'Canada', ''],
                    ].map(([key, label, placeholder, span]) => (
                      <div key={key} className={span}>
                        <Label htmlFor={`addr-${key}`} className="text-xs text-[#5C6670]">{label}</Label>
                        <Input
                          id={`addr-${key}`}
                          value={form.mailing_address[key]}
                          placeholder={placeholder}
                          onChange={(e) => setForm({ ...form, mailing_address: { ...form.mailing_address, [key]: e.target.value } })}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: '#A62B1F', color: 'white' }}>
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? t('common.loading') : t('common.save')}
                  </Button>
                  <Button variant="outline" onClick={() => setEditing(false)}>{t('common.cancel')}</Button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Building2 className="w-4 h-4 text-[#5C6670]" />
                  <div>
                    <p className="text-sm font-medium">{company?.name || 'Not set'}</p>
                    <p className="text-xs text-[#5C6670]">Company Name</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-4 h-4 text-[#5C6670]" />
                  <div>
                    <p className="text-sm font-medium">{company?.slug || 'Not set'}</p>
                    <p className="text-xs text-[#5C6670]">Slug</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#5C6670] mt-0.5" />
                  <div>
                    {isAddressComplete(company?.mailing_address) ? (
                      <p className="text-sm font-medium">{formatAddress(company.mailing_address)}</p>
                    ) : (
                      <p className="text-sm font-medium text-amber-700 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Not set — required for campaign emails
                      </p>
                    )}
                    <p className="text-xs text-[#5C6670]">Mailing Address (campaign email footer)</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-4 h-4 text-[#5C6670]" />
                  <div>
                    <Badge variant={company?.status === 'active' ? 'default' : 'secondary'}>
                      {company?.status || 'active'}
                    </Badge>
                    <p className="text-xs text-[#5C6670] mt-0.5">Status</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Target Types Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Tags className="w-5 h-5 text-[#A62B1F]" />
                <div>
                  <CardTitle>Contact Target Types</CardTitle>
                  <CardDescription>Define the categories of contacts or targets for your company. These appear in the Contacts, Pipeline, and Campaigns views.</CardDescription>
                </div>
              </div>
              <Button
                size="sm"
                onClick={saveContactTypes}
                disabled={savingTypes}
                style={{ backgroundColor: '#A62B1F', color: 'white' }}
              >
                <Save className="w-3.5 h-3.5 mr-1.5" />
                {savingTypes ? 'Saving…' : 'Save Types'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Existing types as removable chips */}
            <div className="flex flex-wrap gap-2 mb-4 min-h-[40px]">
              {contactTypes.length === 0 && (
                <p className="text-sm text-[#9AA3AB] italic">No types defined yet — add your first one below.</p>
              )}
              {contactTypes.map((label) => (
                <Badge
                  key={label}
                  variant="secondary"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#F7ECEA] text-[#A62B1F] border border-[#E7C3BC] rounded-full"
                >
                  {label}
                  <button
                    onClick={() => removeContactType(label)}
                    className="ml-0.5 hover:text-red-500 transition-colors"
                    aria-label={`Remove ${label}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>

            {/* Add new type */}
            <div className="flex gap-2 max-w-sm">
              <Input
                placeholder="e.g. Artists, Labels, DJs…"
                value={newTypeName}
                onChange={e => setNewTypeName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addContactType(); } }}
                className="text-sm"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={addContactType}
                disabled={!newTypeName.trim()}
                className="flex-shrink-0"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            <p className="text-xs text-[#9AA3AB] mt-2">Press Enter or click Add. Click a type's × to remove it. Save when done.</p>
          </CardContent>
        </Card>

        {/* AI Features Card: only when Stagehead has switched AI translation on (it costs to run) */}
        {hasModule(modules, 'translation') && <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-[#A62B1F]" />
              <div>
                <CardTitle>AI Features</CardTitle>
                <CardDescription>AI-assisted tools available across Stagehead OS</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-between gap-4 p-3 bg-[#F3EFE7] rounded-lg">
              <div>
                <p className="text-sm font-medium text-[#1E2A33]">AI Translation for Campaigns</p>
                <p className="text-xs text-[#5C6670] mt-0.5">
                  Lets anyone composing a campaign step generate a translated version (e.g. Spanish for
                  Mexico/Spain outreach) to review, edit, and save. Contacts with a matching Language set
                  receive that version automatically when the campaign sends.
                </p>
              </div>
              <Switch
                checked={aiTranslationEnabled}
                onCheckedChange={toggleAiTranslation}
                disabled={savingAiTranslation}
              />
            </div>
          </CardContent>
        </Card>}

        {/* Email routing info card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-[#A62B1F]" />
              <div>
                <CardTitle>Email Sending</CardTitle>
                <CardDescription>How outbound emails are handled in Stagehead OS</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-[#F3EFE7] rounded-lg">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#1E2A33]">Sequence emails</p>
                <p className="text-xs text-[#5C6670]">Configured per sequence — each sequence uses its own sender account (Gmail, Outlook, or custom SMTP). Manage this inside each sequence's settings.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-[#F3EFE7] rounded-lg">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#1E2A33]">Notifications &amp; alerts</p>
                <p className="text-xs text-[#5C6670]">Sent automatically via the Stagehead OS email service (heydayhq.com). No configuration required.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Wrap>
  );
}
